import { useNavigate } from "react-router-dom";
import { intl } from "LocaleWrapper";
import { useMutation, useQuery } from "@apollo/client";

import { Button, Card, PageHeader, Table } from "@cofano/cck";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";

import Language from "components/utils/Language";
import Helmet from "components/helmet/Helmet";
import { TableId, useFrontendTable } from "components/table";
import { useConfirm } from "components/hooks/useConfirm";

import { usePermission } from "imports/Utils";

import { RelationLink } from "utils/LinkUtils";

import { AllTermsAndConditionsQuery, AllTermsAndConditionsDocument } from "__generated__/view/terms/graphql/queries.generated";
import { DeleteTermsAndConditionsDocument } from "__generated__/view/terms/graphql/mutations.generated";
import { ChildPropsFromArray } from "types/commons";

type TermsAndConditionsFromQuery = ChildPropsFromArray<AllTermsAndConditionsQuery, "allTermsAndConditions">

const TermsAndConditions = () => {
	const navigate = useNavigate();
	const { isReadOnly } = usePermission();
	const confirm = useConfirm();

	const { data: termsAndConditionsData, refetch, loading } = useQuery(AllTermsAndConditionsDocument);
	const [ deleteTermsAndConditions ] = useMutation(DeleteTermsAndConditionsDocument);

	const { tableProps, refetchAfterDelete } = useFrontendTable({
		id: TableId.TermsAndConditions,
		content: termsAndConditionsData?.allTermsAndConditions ?? [],
		paginationEnabled: false,
	});

	const onDelete = (id: number) => {
		deleteTermsAndConditions({
			variables: {
				id: id,
			},
		}).then(() => {
			refetchAfterDelete();
			refetch();
		});
	};

	return <>
		<PageHeader>
			<PageHeader.Title>
				{Language("TERMS_AND_CONDITIONS")}
				<Helmet title={intl.formatMessage({ id: "TERMS_AND_CONDITIONS" })} />
			</PageHeader.Title>

			<PageHeader.Actions>
				<PageHeader.Actions>
					{!isReadOnly && (
						<Button onClick={() => navigate("/view/terms/create")}>
							{intl.formatMessage({ id: "ADD_BUTTON" })}
						</Button>
					)}
				</PageHeader.Actions>
			</PageHeader.Actions>
		</PageHeader>

		<Card disablePadding>
			<Table
				{...tableProps}
				contentLoading={loading}
				columns={[ {
					id: "description",
					title: intl.formatMessage({ id: "DESCRIPTION" }),
				}, {
					id: "debtor",
					title: intl.formatMessage({ id: "CUSTOMER" }),
					render: (row: TermsAndConditionsFromQuery) => <RelationLink relation={row.debtor} />,
				} ]}
				rowActions={isReadOnly ? undefined : [ {
					icon: <EditIcon />,
					onClick: (row: TermsAndConditionsFromQuery) => navigate(`/view/terms/${row.id}/edit`),
				}, {
					icon: <DeleteIcon />,
					onClick: (row: TermsAndConditionsFromQuery) => {
						confirm({
							type: "DELETE",
						}).then(() => {
							onDelete(row.id);
						});
					},
				} ]} />
		</Card>
	</>;
};

export default TermsAndConditions;
